// Tape stuck around a picture, the way a photo gets pinned to a drafting
// board: two or three strips over the corners and sometimes one on an edge,
// each with its own length, lean and torn ends. Everything comes from the
// seed (the project id), so a picture is taped the same way every time and
// no two pictures alike. Ported from the iOS TapeOverlay.
import { useMemo } from 'react';

export interface Strip {
  /** Centre, as fractions of the picture. */
  x: number; y: number;
  angle: number;
  /** Multiples of the strip's thickness (the CSS sets that). */
  length: number;
  /** The torn short ends, as a polygon in percent. */
  clip: string;
}

/** FNV-1a over the seed's characters: stable across devices, unlike a hash code. */
export function hashSeed(seed: string | number): number {
  let h = 2166136261;
  for (const ch of String(seed)) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619) >>> 0; }
  return h;
}

/** A small deterministic generator (mulberry32); `next()` is in [0, 1). */
export function rng(seed: number): () => number {
  let s = seed >>> 0 || 7;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Anchors sit a hair inside the picture so a strip grips the corner and
// overhangs a little rather than floating off into the card around it.
const INSET = 0.07;
const CORNERS: [number, number, number][] = [[INSET, INSET, -45], [1 - INSET, INSET, 45], [INSET, 1 - INSET, 45], [1 - INSET, 1 - INSET, -45]];
const EDGES: [number, number, number][] = [[0.5, INSET, 0], [0.5, 1 - INSET, 0], [INSET, 0.5, 90], [1 - INSET, 0.5, 90]];

function shuffle<T>(list: T[], next: () => number): T[] {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) { const j = Math.floor(next() * (i + 1)); [out[i], out[j]] = [out[j]!, out[i]!]; }
  return out;
}

// Straight long edges, torn short ends: three notches down each end.
function tornEnds(next: () => number): string {
  const n = () => (next() * 6).toFixed(1);
  return `polygon(${n()}% 0, ${100 - +n()}% 0, ${100 - +n()}% 33%, ${100 - +n()}% 67%, ${100 - +n()}% 100%, ${n()}% 100%, ${n()}% 67%, ${n()}% 33%)`;
}

export function tapeStrips(seed: string | number): Strip[] {
  const next = rng(hashSeed(seed));
  const anchors = shuffle(CORNERS, next).slice(0, 2 + (next() < 0.5 ? 1 : 0));
  if (next() < 0.35) anchors.push(shuffle(EDGES, next)[0]!);
  const drift = 0.035;
  return anchors.map(([x, y, angle]) => ({
    x: x + (next() * 2 - 1) * drift,
    y: y + (next() * 2 - 1) * drift,
    angle: angle + (next() * 2 - 1) * 16,
    length: 2 + next() * 1.2,
    clip: tornEnds(next),
  }));
}

export function Tape({ seed }: { seed: string | number }) {
  const strips = useMemo(() => tapeStrips(seed), [seed]);
  return (
    <>
      {strips.map((s, i) => (
        <i
          key={i} className="tape" aria-hidden="true"
          style={{ left: `${s.x * 100}%`, top: `${s.y * 100}%`, width: `calc(var(--tape-h) * ${s.length.toFixed(2)})`, transform: `translate(-50%, -50%) rotate(${s.angle.toFixed(1)}deg)`, clipPath: s.clip }}
        />
      ))}
    </>
  );
}
